import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { CheckRounded, KeyboardArrowDownRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { OUTLINED_INPUT_SX } from "../common/outlinedInputStyles";
import Loader from "../common/loader";
import {
    COUNTRY_SELECT_LISTBOX_SX,
    COUNTRY_SELECT_PAPER_SX,
} from "../common/countrySelectStyles";
import {
    PHONE_DIAL_CODES,
    countryCodeToFlag,
    getPhoneDialEntry,
    parsePhoneDialCountry,
    splitPhoneNumber,
} from "../../utils/phoneDialCodes";
import {
    MAX_INTERNATIONAL_PHONE_DIGITS,
    canRequestPhoneVerificationCode,
    getInternationalPhoneValidationError,
    sanitizePhoneLocalNumber,
} from "./accountFormUtils";
import {
    ACCOUNT_PAGE_FIELD_ERROR_SX,
    ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX,
    ACCOUNT_PAGE_FIELD_LABEL_SX,
    ACCOUNT_PAGE_MOBILE_MQ,
    ACCOUNT_PAGE_FIELD_ROW_SX,
    ACCOUNT_PAGE_PHONE_VERIFIED_INPUT_SX,
    ACCOUNT_PAGE_SEND_CODE_BUTTON_DISABLED_SX,
    ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
} from "./accountPageStyles";
import { colors } from "../../theme/colors";
import { toast } from "../../universal/toast";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

function getTerminalPhoneVerificationStatus(message) {
    if (message === "Verification code has expired") return "expired";
    if (message === "Too many incorrect attempts") return "expired";
    return null;
}

function isActivePhoneVerificationMessage(message) {
    return (
        message ===
        "A verification code has already been sent to your phone. Please enter it below."
    );
}

const PHONE_INPUT_SX = {
    ...OUTLINED_INPUT_SX,
    "& .MuiOutlinedInput-input": {
        ...OUTLINED_INPUT_SX["& .MuiOutlinedInput-input"],
        paddingLeft: "0.35rem",
    },
};

function composeInternationalPhone(dialCountry, localNumber) {
    const dialCode = getPhoneDialEntry(dialCountry)?.dialCode || "";
    const local = String(localNumber || "").trim();
    if (!local) return dialCode;
    if (local.startsWith("+")) return local;
    return `${dialCode}${local}`;
}

function normalizePhoneForComparison(phoneNumber) {
    if (typeof phoneNumber !== "string") return "";

    const trimmed = phoneNumber.trim();
    if (!trimmed) return "";

    if (trimmed.startsWith("+")) {
        return `+${trimmed.slice(1).replace(/\D/g, "")}`;
    }

    return trimmed.replace(/\s+/g, " ");
}

const OPEN_PHONE_CHALLENGE_STATUSES = new Set([
    "code_sent",
    "phone_verification_ready",
    "expired",
    "not_started",
    "pending",
]);

function normalizePhoneVerificationStatus(value) {
    if (typeof value !== "string") return null;
    const normalized = value.trim().toLowerCase();
    return normalized || null;
}

function getServerPhoneVerification(user) {
    const raw = user?.phoneVerification;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
    return null;
}

function isPhoneNumberVerified({
    status,
    phoneVerification,
    storedPhone,
    accountStatus,
}) {
    const normalized = normalizePhoneVerificationStatus(status);
    if (normalized === "verified") return true;
    if (phoneVerification?.verifiedAt) return true;
    if (OPEN_PHONE_CHALLENGE_STATUSES.has(normalized)) return false;
    return (
        Boolean(String(storedPhone || "").trim()) &&
        (accountStatus === "verified" || accountStatus === "active")
    );
}

function AccountPhoneField({ user, actions }) {
    const storedPhone = typeof user?.phoneNumber === "string" ? user.phoneNumber : "";
    const initialDial = parsePhoneDialCountry(storedPhone, user?.country);
    const [dialCountry, setDialCountry] = useState(initialDial);
    const [localNumber, setLocalNumber] = useState(
        splitPhoneNumber(storedPhone, initialDial).localNumber,
    );
    const [validationError, setValidationError] = useState("");
    const [initiating, setInitiating] = useState(false);
    const [terminalStatus, setTerminalStatus] = useState(null);
    const [verifyCode, setVerifyCode] = useState("");
    const [verifyError, setVerifyError] = useState("");
    const [verifySubmitting, setVerifySubmitting] = useState(false);
    const [optimisticVerification, setOptimisticVerification] = useState(null);
    const phoneInputRef = useRef(null);
    const phoneMenuActionRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        let animationFrameId = null;
        const updateMenuPosition = () => {
            if (!phoneMenuActionRef.current || animationFrameId !== null) return;

            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                phoneMenuActionRef.current?.updatePosition();
            });
        };

        // Account content can scroll inside containers whose scroll events do
        // not bubble to window. Capture all scrolls so the menu remains attached
        // to the full phone field while the page moves.
        window.addEventListener("scroll", updateMenuPosition, true);
        return () => {
            window.removeEventListener("scroll", updateMenuPosition, true);
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, []);

    useEffect(() => {
        const nextDial = parsePhoneDialCountry(storedPhone, user?.country);
        setDialCountry(nextDial);
        setLocalNumber(splitPhoneNumber(storedPhone, nextDial).localNumber);
        setValidationError("");
        setVerifyCode("");
        setVerifyError("");
        setOptimisticVerification(null);
        setTerminalStatus(null);
    }, [storedPhone, user?.country]);

    const composedPhone = useMemo(
        () => composeInternationalPhone(dialCountry, localNumber),
        [dialCountry, localNumber],
    );
    const maxLocalNumberDigits = useMemo(() => {
        const dialDigits = String(
            getPhoneDialEntry(dialCountry)?.dialCode || "",
        ).replace(/\D/g, "").length;
        return Math.max(0, MAX_INTERNATIONAL_PHONE_DIGITS - dialDigits);
    }, [dialCountry]);
    const phoneValidationError = useMemo(
        () => getInternationalPhoneValidationError(composedPhone),
        [composedPhone],
    );

    const phoneVerification = getServerPhoneVerification(user);
    const phoneVerificationStatus = useMemo(() => {
        const serverStatus = normalizePhoneVerificationStatus(
            phoneVerification?.status,
        );
        if (terminalStatus) return terminalStatus;
        if (serverStatus === "code_sent") return "code_sent";
        return (
            normalizePhoneVerificationStatus(optimisticVerification?.status) ||
            serverStatus
        );
    }, [
        optimisticVerification?.status,
        phoneVerification?.status,
        terminalStatus,
    ]);

    const isStoredPhoneVerified = isPhoneNumberVerified({
        status: phoneVerificationStatus,
        phoneVerification,
        storedPhone,
        accountStatus: user?.accountStatus,
    });
    const phoneChanged =
        normalizePhoneForComparison(composedPhone) !==
        normalizePhoneForComparison(storedPhone);
    const isCurrentPhoneVerified = isStoredPhoneVerified && !phoneChanged;
    const currentPhoneVerificationStatus = phoneChanged
        ? "not_started"
        : phoneVerificationStatus;
    const isCodeSent =
        !phoneChanged && phoneVerificationStatus === "code_sent";
    const canSendCode =
        !isCurrentPhoneVerified &&
        canRequestPhoneVerificationCode(
            user?.accountStatus,
            currentPhoneVerificationStatus,
        );
    const sendDisabled =
        isCurrentPhoneVerified ||
        !canSendCode ||
        Boolean(phoneValidationError) ||
        initiating ||
        !user?.userId;

    const showError = Boolean(validationError);

    const handleLocalChange = useCallback((event) => {
        const nextValue = sanitizePhoneLocalNumber(
            event.target.value,
            maxLocalNumberDigits,
        );
        setLocalNumber(nextValue);
        setVerifyCode("");
        setVerifyError("");
        setValidationError((current) => {
            if (!current) return "";
            return (
                getInternationalPhoneValidationError(
                    composeInternationalPhone(dialCountry, nextValue),
                ) || ""
            );
        });
    }, [dialCountry, maxLocalNumberDigits]);

    const handleDialChange = useCallback((event) => {
        const nextDial = event.target.value;
        setDialCountry(nextDial);
        setVerifyCode("");
        setVerifyError("");
        setValidationError((current) => {
            if (!current) return "";
            return (
                getInternationalPhoneValidationError(
                    composeInternationalPhone(nextDial, localNumber),
                ) || ""
            );
        });
    }, [localNumber]);

    const handlePhoneBlur = useCallback(() => {
        if (!String(localNumber || "").trim() && !validationError) return;
        setValidationError(
            getInternationalPhoneValidationError(composedPhone) || "",
        );
    }, [composedPhone, localNumber, validationError]);

    const handleSendCode = useCallback(async () => {
        if (sendDisabled) return;

        const error = getInternationalPhoneValidationError(composedPhone);
        if (error) {
            setValidationError(error);
            return;
        }

        setValidationError("");
        setInitiating(true);

        try {
            const phoneChanged =
                normalizePhoneForComparison(composedPhone) !==
                normalizePhoneForComparison(storedPhone);

            if (phoneChanged) {
                if (typeof actions?.submitAccountChanges !== "function") {
                    setInitiating(false);
                    toast.error(
                        "Unable to save your phone number. Please try again.",
                    );
                    return;
                }

                const saveResult = await actions.submitAccountChanges({
                    userId: user?.userId,
                    phoneNumber: composedPhone,
                });

                if (saveResult?.type !== "ACCOUNT_UPDATE_COMPLETE") {
                    setInitiating(false);
                    toast.error(
                        saveResult?.text ||
                            "Failed to save your phone number. Please try again.",
                    );
                    return;
                }
            }

            const res = await fetch(
                `${basePath}/api/v1/account/initiate-phone-verification`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ provider: "twilio" }),
                },
            );
            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data?.ok) {
                const errorMessage =
                    (typeof data?.message === "string" && data.message) ||
                    "Failed to send verification code. Please try again.";
                const hasExistingActiveCode =
                    isActivePhoneVerificationMessage(errorMessage);

                setInitiating(false);
                if (hasExistingActiveCode) {
                    toast.info(errorMessage);
                    setTerminalStatus("code_sent");
                    try {
                        await actions?.refreshSessionUser?.();
                    } catch (_) {
                        // Keep local code_sent so the OTP field still appears.
                    }
                } else {
                    toast.error(errorMessage);
                }
                return;
            }

            setInitiating(false);
            setTerminalStatus(null);
            toast.success(
                "Verification code sent. Check your phone for the 6-digit code.",
            );
            try {
                await actions?.refreshSessionUser?.();
            } catch (_) {
                setTerminalStatus("code_sent");
            }
        } catch (_) {
            setInitiating(false);
            toast.error("Network error. Please try again.");
        }
    }, [actions, composedPhone, sendDisabled, storedPhone, user?.userId]);

    const handleVerifySubmit = useCallback(async () => {
        const digits = String(verifyCode || "").replace(/\D/g, "");
        if (digits.length !== 6) {
            setVerifyError("Enter the 6-digit code from your text message.");
            return;
        }

        setVerifySubmitting(true);
        setVerifyError("");

        try {
            const res = await fetch(`${basePath}/api/v1/account/verify-phone`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: digits }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data?.ok) {
                const errorMessage =
                    (typeof data?.message === "string" && data.message) ||
                    "Verification failed. Please try again.";
                const nextTerminal =
                    getTerminalPhoneVerificationStatus(errorMessage);

                setVerifySubmitting(false);
                setVerifyError("");
                toast.error(errorMessage);
                if (nextTerminal) {
                    setVerifyCode("");
                    setTerminalStatus(nextTerminal);
                    try {
                        await actions?.refreshSessionUser?.();
                    } catch (_) {
                        // Local terminal state already collapsed the OTP field.
                    }
                }
                return;
            }

            setVerifySubmitting(false);
            setVerifyCode("");
            setVerifyError("");
            setTerminalStatus(null);
            setOptimisticVerification(
                data?.phoneVerification && typeof data.phoneVerification === "object"
                    ? data.phoneVerification
                    : { status: "verified" },
            );
            toast.success("Phone number verified successfully.");
            try {
                await actions?.refreshSessionUser?.();
            } catch (_) {
                // Keep optimistic verified until the next refresh.
            }
        } catch (_) {
            setVerifySubmitting(false);
            setVerifyError("");
            toast.error("Network error. Please try again.");
        }
    }, [actions, verifyCode]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.45rem",
                width: "100%",
            }}
        >
            <Typography
                component="label"
                htmlFor="account-phone-number"
                sx={ACCOUNT_PAGE_FIELD_LABEL_SX}
            >
                Phone number
            </Typography>
            <Box
                sx={{
                    ...ACCOUNT_PAGE_FIELD_ROW_SX,
                    [ACCOUNT_PAGE_MOBILE_MQ]: {
                        flexDirection: "column",
                        alignItems: "stretch",
                    },
                }}
            >
                <Box
                    sx={{
                        ...ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX,
                        position: "relative",
                        [ACCOUNT_PAGE_MOBILE_MQ]: {
                            maxWidth: "100%",
                        },
                    }}
                >
                    <TextField
                        ref={phoneInputRef}
                        id="account-phone-number"
                        variant="outlined"
                        type="tel"
                        value={localNumber}
                        error={showError && !isCurrentPhoneVerified}
                        onChange={handleLocalChange}
                        onBlur={handlePhoneBlur}
                        placeholder="Local number"
                        sx={[
                            PHONE_INPUT_SX,
                            isCurrentPhoneVerified
                                ? ACCOUNT_PAGE_PHONE_VERIFIED_INPUT_SX
                                : null,
                            {
                                "& .MuiOutlinedInput-input": {
                                    cursor: "text",
                                    paddingRight: isCurrentPhoneVerified
                                        ? "2.1rem"
                                        : undefined,
                                },
                            },
                        ]}
                        slotProps={{
                            htmlInput: {
                                "aria-label": "Phone number",
                                "aria-invalid":
                                    showError && !isCurrentPhoneVerified,
                                "aria-describedby": showError
                                    ? "account-phone-number-error"
                                    : undefined,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                maxLength: maxLocalNumberDigits,
                                spellCheck: false,
                            },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 0 }}>
                                        <Select
                                            value={dialCountry}
                                            onChange={handleDialChange}
                                            variant="standard"
                                            disableUnderline
                                            IconComponent={KeyboardArrowDownRounded}
                                            renderValue={(val) => {
                                                const entry = PHONE_DIAL_CODES.find(
                                                    (c) => c.code === val,
                                                );
                                                if (!entry) return val;
                                                return `${countryCodeToFlag(entry.code)} ${entry.dialCode}`;
                                            }}
                                            MenuProps={{
                                                action: phoneMenuActionRef,
                                                anchorEl: () => phoneInputRef.current,
                                                anchorOrigin: {
                                                    vertical: "bottom",
                                                    horizontal: "left",
                                                },
                                                transformOrigin: {
                                                    vertical: "top",
                                                    horizontal: "left",
                                                },
                                                disableScrollLock: true,
                                                marginThreshold: null,
                                                PaperProps: {
                                                    sx: () => ({
                                                        ...COUNTRY_SELECT_PAPER_SX,
                                                        width: phoneInputRef.current
                                                            ? `${phoneInputRef.current.clientWidth}px`
                                                            : "18rem",
                                                        maxWidth:
                                                            "calc(100vw - 2rem)",
                                                        maxHeight: "18.5rem",
                                                    }),
                                                },
                                                MenuListProps: {
                                                    onWheel: (event) => {
                                                        event.stopPropagation();
                                                    },
                                                    sx: COUNTRY_SELECT_LISTBOX_SX,
                                                },
                                            }}
                                            sx={{
                                                mr: "0.45rem",
                                                color: colors.text.primary,
                                                "&.Mui-disabled": {
                                                    color: colors.text.primary,
                                                    WebkitTextFillColor:
                                                        colors.text.primary,
                                                    opacity: 1,
                                                },
                                                "& .MuiSelect-select": {
                                                    py: 0,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.35rem",
                                                    pr: "1.4rem !important",
                                                    fontSize: "0.82rem",
                                                    cursor: "pointer",
                                                },
                                                "& .MuiSelect-icon": {
                                                    display: "block",
                                                    color: colors.text.muted,
                                                    fontSize: "1.15rem",
                                                    right: "-0.1rem",
                                                },
                                            }}
                                        >
                                            {PHONE_DIAL_CODES.map((entry) => (
                                                <MenuItem
                                                    key={entry.code}
                                                    value={entry.code}
                                                    sx={{
                                                        fontSize: "0.85rem",
                                                        color: colors.text.primary,
                                                        "&.Mui-selected": {
                                                            backgroundColor: `${alpha(colors.primary.main, 0.24)} !important`,
                                                            color: colors.accent.main,
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            backgroundColor: `${alpha(colors.primary.main, 0.32)} !important`,
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{ mr: 1, fontSize: "1.05rem" }}
                                                    >
                                                        {countryCodeToFlag(entry.code)}
                                                    </Box>
                                                    {entry.name}
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            ml: 0.75,
                                                            color: colors.text.muted,
                                                        }}
                                                    >
                                                        {entry.dialCode}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    {isCurrentPhoneVerified ? (
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                top: 0,
                                right: "0.7rem",
                                height: "2.35rem",
                                display: "flex",
                                alignItems: "center",
                                pointerEvents: "none",
                            }}
                        >
                            <CheckRounded
                                aria-label="Phone verified"
                                sx={{
                                    color: colors.state.success.main,
                                    fontSize: "1.25rem",
                                }}
                            />
                        </Box>
                    ) : null}
                    {showError ? (
                        <Typography
                            id="account-phone-number-error"
                            role="alert"
                            sx={ACCOUNT_PAGE_FIELD_ERROR_SX}
                        >
                            {validationError}
                        </Typography>
                    ) : null}
                </Box>
                <Box
                    component="button"
                    type="button"
                    aria-label="Send code"
                    disabled={sendDisabled}
                    aria-disabled={sendDisabled}
                    aria-busy={initiating}
                    onClick={handleSendCode}
                    sx={[
                        ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
                        {
                            [ACCOUNT_PAGE_MOBILE_MQ]: {
                                width: "100%",
                            },
                        },
                        sendDisabled && !initiating
                            ? ACCOUNT_PAGE_SEND_CODE_BUTTON_DISABLED_SX
                            : null,
                    ]}
                >
                    {initiating ? (
                        <Loader size={18} />
                    ) : null}
                    Send code
                </Box>
            </Box>

            {isCodeSent ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        mt: "0.35rem",
                        width: "100%",
                    }}
                >
                    <Box sx={ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX}>
                        <TextField
                            id="account-phone-verify-code"
                            variant="outlined"
                            value={verifyCode}
                            error={Boolean(verifyError)}
                            onChange={(event) => {
                                setVerifyCode(
                                    String(event.target.value || "")
                                        .replace(/\D/g, "")
                                        .slice(0, 6),
                                );
                                setVerifyError("");
                            }}
                            placeholder="6-digit code"
                            sx={OUTLINED_INPUT_SX}
                            slotProps={{
                                htmlInput: {
                                    "aria-label": "Phone verification code",
                                    inputMode: "numeric",
                                    maxLength: 6,
                                },
                            }}
                        />
                        {verifyError ? (
                            <Typography sx={ACCOUNT_PAGE_FIELD_ERROR_SX}>
                                {verifyError}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        aria-label="Verify code"
                        disabled={verifySubmitting}
                        aria-disabled={verifySubmitting}
                        aria-busy={verifySubmitting}
                        onClick={handleVerifySubmit}
                        sx={ACCOUNT_PAGE_SEND_CODE_BUTTON_SX}
                    >
                        {verifySubmitting ? (
                            <Loader size={18} />
                        ) : null}
                        Verify
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}

AccountPhoneField.propTypes = {
    user: PropTypes.object,
    actions: PropTypes.object,
};

export default React.memo(AccountPhoneField);
